/**
 * Confirm an Action — client-side entity executor (ADR-029 §4).
 *
 * Runs in the HOST page, never in the plugin iframe. When a plugin calls a
 * verb that acts outside Fias in the person's name, the server runs nothing
 * and records a pending action; the SDK then invokes this entity with its id.
 * The host draws its own confirm sheet from the server's preview — platform-
 * authored content, the "who draws the pixels" rule — and the person's answer
 * is written by the host session. The plugin learns only whether it was
 * approved.
 *
 * Host services arrive through `configureActionConfirmationHost` at
 * registration (`client/src/lib/register-client-entities.ts`), because the
 * executor context carries only `{ userId, archeId }`.
 *
 * Expected failures are RESULTS, not throws, so a typed code reaches the SDK.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const ACTION_CONFIRMATION_ENTITY_ID = 'ent_intg_action_confirmation_000';

const PENDING_ACTION_ID = /^pact_[a-f0-9]{32}$/;

export interface ActionConfirmationInput {
  pendingActionId: string;
}

export type ActionConfirmationErrorCode =
  | 'INVALID_INPUT'
  | 'CONFIRM_UNAVAILABLE'
  | 'ACTION_NOT_FOUND'
  | 'SHEET_BUSY';

export type ActionConfirmationResult =
  | { approved: boolean }
  | { error: { code: ActionConfirmationErrorCode; message: string } };

/** What the host page provides: the sheet, drawn for one arche. */
export interface ActionConfirmationHost {
  showConfirmSheet(request: {
    archeId: string;
    pendingActionId: string;
  }): Promise<ActionConfirmationResult>;
}

let host: ActionConfirmationHost | null = null;

/** Called once by the host page at registration. */
export function configureActionConfirmationHost(next: ActionConfirmationHost | null): void {
  host = next;
}

export async function actionConfirmationExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<ActionConfirmationResult> {
  const pendingActionId =
    input && typeof input === 'object'
      ? (input as { pendingActionId?: unknown }).pendingActionId
      : undefined;
  if (typeof pendingActionId !== 'string' || !PENDING_ACTION_ID.test(pendingActionId)) {
    return {
      error: { code: 'INVALID_INPUT', message: 'pendingActionId must be a pending action id' },
    };
  }
  if (!host) {
    return {
      error: {
        code: 'CONFIRM_UNAVAILABLE',
        message: 'Approving actions is not available here',
      },
    };
  }
  return host.showConfirmSheet({ archeId: context.archeId, pendingActionId });
}

export const actionConfirmationDefinition = {
  entityId: ACTION_CONFIRMATION_ENTITY_ID,
  rateLimitPerMinute: 10,
  pluginPermission: 'connections:use',
  executor: actionConfirmationExecutor,
} as const satisfies ClientEntityDefinition<unknown, ActionConfirmationResult>;
